import CachedData from '@/lib/cached-data'
import * as Constants from '@/lib/constants'
import PurchaseInfo from '@/lib/purchase-info'
import UTCDateTime from '@/lib/utc-datetime'
import moment from 'moment'

/**
 * Storage
 */
class Storage {
  putCache(key, value) {
    const cacheLabel = Constants.STORAGE_CACHE_KEY_PREFIX + key
    let cache = new CachedData()
    cache.touch()
    cache.value = value
    localStorage.setItem(cacheLabel, JSON.stringify(cache))
  }

  getCache(key) {
    const cacheLabel = Constants.STORAGE_CACHE_KEY_PREFIX + key
    const cacheString = localStorage.getItem(cacheLabel)
    if (cacheString) {
      try {
        const parsedObject = JSON.parse(cacheString)
        const timestamp = parsedObject.timestamp
        const cacheValue = parsedObject.value
        const cache = new CachedData(timestamp, cacheValue)
        if (cache.isExpired()) {
          localStorage.removeItem(cacheLabel)
          return null
        }
        return cache.value
      } catch (error) {
        console.error(error)
        return null
      }
    }
    return null
  }

  getPurchaseInfo() {
    if (localStorage.getItem(Constants.KEY_PURCHASE_INFO)) {
      return new PurchaseInfo(JSON.parse(localStorage.getItem(Constants.KEY_PURCHASE_INFO)))
    }
    // PurchaseInfoが取れるまで時差があるので、ダミーを返す（初回）
    return PurchaseInfo.dummy()
  }

  savePurchaseInfo(purchaseInfo) {
    localStorage.setItem(Constants.KEY_PURCHASE_INFO, JSON.stringify(purchaseInfo))
  }

  getUserLang() {
    return localStorage.getItem(Constants.USER_LANG_KEY)
  }

  getUserLangConsideringDefault() {
    const userLang = this.getUserLang()
    if (userLang) {
      return userLang
    }
    return Constants.LANG_DEFAULT
  }

  saveUserLanguage(language) {
    localStorage.setItem(Constants.USER_LANG_KEY, language)
  }

  saveRememberMe(rememberMe) {
    localStorage.setItem(Constants.KEY_LOGIN_REMEMBER_ME, rememberMe)
  }

  isLoginRemmberMe() {
    const value = localStorage.getItem(Constants.KEY_LOGIN_REMEMBER_ME)
    return value === 'true'
  }

  saveRefreshTokens(refreshToken, refreshTokenExpiredAt) {
    let exp = null
    if (typeof refreshTokenExpiredAt === 'string') {
      const utc = UTCDateTime.toUtcMoment(refreshTokenExpiredAt)
      exp = UTCDateTime.utcStringFromMomentObject(utc)
    } else {
      exp = UTCDateTime.utcStringFromMomentObject(refreshTokenExpiredAt)
    }
    if (this.isLoginRemmberMe()) {
      localStorage.setItem(Constants.KEY_REFRESH_TOKEN, refreshToken)
      localStorage.setItem(Constants.KEY_REFRESH_TOKEN_EXPIRED_AT, exp)
      sessionStorage.removeItem(Constants.KEY_REFRESH_TOKEN)
      sessionStorage.removeItem(Constants.KEY_REFRESH_TOKEN_EXPIRED_AT)
    } else {
      sessionStorage.setItem(Constants.KEY_REFRESH_TOKEN, refreshToken)
      sessionStorage.setItem(Constants.KEY_REFRESH_TOKEN_EXPIRED_AT, exp)
      localStorage.removeItem(Constants.KEY_REFRESH_TOKEN)
      localStorage.removeItem(Constants.KEY_REFRESH_TOKEN_EXPIRED_AT)
    }
  }

  saveUserInfo(userInfo) {
    localStorage.setItem(Constants.KEY_USER_INFO, JSON.stringify(userInfo))
    this.saveUserLanguage(userInfo.language)
  }

  getUserInfo() {
    const json = localStorage.getItem(Constants.KEY_USER_INFO)
    try {
      const userInfo = JSON.parse(json)
      return userInfo
    } catch (error) {
      console.error('userinfo parse error', error)
    }
    return null
  }

  getUserInfoRequestToPay() {
    const userInfo = this.getUserInfo()
    const userInfoRequest = {
      stripeId: userInfo.stripeId,
      email: userInfo.email,
      language: userInfo.language,
      enforcedPlan: userInfo.enforcedPlan,
      functionality: userInfo.functionality,
      trialAvailable: userInfo.trialAvailable,
      nickname: userInfo.nickname,
    }
    return userInfoRequest
  }

  getRefreshToken() {
    let token = localStorage.getItem(Constants.KEY_REFRESH_TOKEN)
    if (!token) {
      token = sessionStorage.getItem(Constants.KEY_REFRESH_TOKEN)
    }
    if (token) {
      let expValue = localStorage.getItem(Constants.KEY_REFRESH_TOKEN_EXPIRED_AT)
      if (!expValue) {
        expValue = sessionStorage.getItem(Constants.KEY_REFRESH_TOKEN_EXPIRED_AT)
      }
      if (expValue) {
        const exp = moment(expValue)
        const now = moment()
        const expired = exp.isBefore(now)
        if (expired) {
          console.error('refresh token is expired')
          return null
        } else {
          return token
        }
      }
    }
    return null
  }

  removeRefreshTokens() {
    localStorage.clear()
    sessionStorage.clear()
  }
}

const storage = new Storage()
export default storage
